import { DestroyRef, Injectable, computed, inject, signal } from '@angular/core';
import {
  DARK_SCHEME_QUERY,
  THEME,
  THEME_PREFERENCE,
  THEME_STORAGE_KEY,
  Theme,
  ThemePreference,
} from '../models/theme.model';

const STORED_PREFERENCES: string[] = Object.values(THEME_PREFERENCE);

@Injectable({ providedIn: 'root' })
export class ThemeService {
  //* Services
  private readonly _destroyRef = inject(DestroyRef);

  //* Signals
  private readonly _preference = signal<ThemePreference>(THEME_PREFERENCE.SYSTEM);
  private readonly _systemPrefersDark = signal<boolean>(false);

  //* Public readonly / Computed
  public readonly preference = this._preference.asReadonly();

  public readonly theme = computed<Theme>(() => {
    const preference = this._preference();
    if (preference === THEME_PREFERENCE.SYSTEM) {
      return this._systemPrefersDark() ? THEME.DARK : THEME.LIGHT;
    }
    return preference;
  });

  public readonly isDark = computed<boolean>(() => this.theme() === THEME.DARK);

  //* Lifecycle
  constructor() {
    this._init();
  }

  //* Public Methods
  public toggle(): void {
    this.setPreference(this.isDark() ? THEME.LIGHT : THEME.DARK);
  }

  public setTheme(theme: Theme): void {
    this.setPreference(theme);
  }

  public setPreference(preference: ThemePreference): void {
    this._preference.set(preference);
    this._applyClass();
    this._persist(preference);
  }

  //* Private Methods
  private _init(): void {
    this._preference.set(this._readStored() ?? THEME_PREFERENCE.SYSTEM);

    const media = this._darkSchemeQuery();
    this._systemPrefersDark.set(media?.matches ?? false);
    this._applyClass();

    if (!media) return;
    const onSchemeChange = (event: MediaQueryListEvent): void => {
      this._systemPrefersDark.set(event.matches);
      this._applyClass();
    };
    media.addEventListener('change', onSchemeChange);
    this._destroyRef.onDestroy(() => media.removeEventListener('change', onSchemeChange));
  }

  private _applyClass(): void {
    document.documentElement.classList.toggle('dark', this.isDark());
  }

  private _readStored(): ThemePreference | null {
    try {
      const value = window.localStorage.getItem(THEME_STORAGE_KEY);
      return value && STORED_PREFERENCES.includes(value) ? (value as ThemePreference) : null;
    } catch {
      return null;
    }
  }

  private _persist(preference: ThemePreference): void {
    try {
      window.localStorage.setItem(THEME_STORAGE_KEY, preference);
    } catch {
      return;
    }
  }

  private _darkSchemeQuery(): MediaQueryList | null {
    try {
      return window.matchMedia(DARK_SCHEME_QUERY);
    } catch {
      return null;
    }
  }
}
